<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="dataItems"
      :options.sync="options"
      :footer-props="{
        'items-per-page-options': [10, 20]
      }"
      :server-items-length="totalEvents"
      :loading="loading"
      class="elevation-1 row-pointer"

      @click:row="handleClick"
    >
      <template #[`item.name`]="{item}">
        <div class="d-flex align-center">
          <v-avatar
            size="33"
            class="me-3"
            tile
          >
            <v-img
              :src="item.firstImage || TSS_IMAGE_PLACEHOLDER"
              @error="item.firstImage = TSS_IMAGE_PLACEHOLDER"
            ></v-img>
          </v-avatar>
          <div class="text-no-wrap">
            <p class="font-weight-medium text--primary mb-n1">
              {{ item.name }}
            </p>
          </div>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { formatOptions } from '@/services/datatables/datatables.service';
import { getFormattedDateFromIso } from '@secret-society/datetime';
import { EVENT_PAGE } from '@/router/constants';
import {
  ref, computed, watch,
} from '@vue/composition-api';
import { useNamespacedGetters } from 'vuex-composition-helpers';
import { useRouter } from '@core/utils';
import { TSS_IMAGE_PLACEHOLDER } from '@/constants/global.constants';
import { fetchEventsFromEstablishment } from '@/services/events/events.service';

export default {
  name: 'EventsList',
  setup() {
    const { router } = useRouter();

    // State
    const currentId = ref(null);
    const totalEvents = ref(0);
    const dataItems = ref([]);
    const loading = ref(true);
    const headers = ref([
      {
        text: 'Name',
        align: 'start',
        value: 'name',
        sortable: false,
      },
      { text: 'Date', value: 'start_at', align: 'start' },
      {
        text: 'Hours', value: 'timeFull', align: 'start', sortable: false,
      },
    ]);
    const options = ref({
      itemsPerPage: 10,
    });

    // Computed
    const { getCurrentEstablishment } = useNamespacedGetters('establishmentsStore', ['getCurrentEstablishment']);
    const getCurrentEstablishmentId = computed(() => getCurrentEstablishment?.value?.id);

    // Methods
    const getDataFromApi = async () => {
      loading.value = true;
      const { page, itemsPerPage, sorting } = formatOptions(options.value);
      const { data, paging } = await fetchEventsFromEstablishment(getCurrentEstablishmentId.value, {
        sort: sorting,
        page,
        size: itemsPerPage,
      });

      dataItems.value = data.map((item) => {
        const startAtDateHuman = getFormattedDateFromIso(item.start_at).human();
        const endAtDateHuman = getFormattedDateFromIso(item.end_at).human();

        return {
          ...item,
          start_at: startAtDateHuman.dateLongYear,
          start_at_time: startAtDateHuman.time,
          end_at_time: endAtDateHuman.time,
          timeFull: `${startAtDateHuman.time} -> ${endAtDateHuman.time}`,
          status: item.computed.isLive ? 'is_live' : 'is_passed',
        };
      });

      totalEvents.value = paging.count;
      loading.value = false;
    };

    const handleClick = (row) => {
      router.push({ name: EVENT_PAGE, params: { eventId: row.id } });
    };

    // Watchers
    const getCurrentEstablishmentIdWatch = watch(getCurrentEstablishmentId, () => {
      getDataFromApi();
    });

    const optionsWatch = watch(options, () => {
      if (getCurrentEstablishment.value) {
        getDataFromApi();
      }
    });

    // return to template
    return {
      // state
      currentId,
      totalEvents,
      dataItems,
      loading,
      headers,
      options,

      // computed
      getCurrentEstablishment,
      getCurrentEstablishmentId,

      // Methods
      getDataFromApi,
      handleClick,

      // Watchers
      getCurrentEstablishmentIdWatch,
      optionsWatch,

      // Others
      TSS_IMAGE_PLACEHOLDER,

    };
  },
};
</script>

<style scoped>
.row-pointer >>> tbody tr :hover {
  cursor: pointer;
}
</style>
